import {
	infiniteQueryOptions,
	queryOptions,
	useQueries,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'

import { getAutomationsList } from '@/features/air/automation-settings/api'
import { getCaseList } from '@/features/air/case-management/api'
import { toCaseListParams } from '@/features/air/case-management/lib/case-list-params'
import { getPolicyList } from '@/features/air/policy-management/api'
import { integrationQueries } from '@/features/integrations/queries'

import { fingerprint, successStaleTime } from '@/shared/api/utils/query'
import { userKeys } from '@/shared/auth/queries'
import { tenantSessionQueries } from '@/shared/auth/tenant-session'
import { getCurrentTenant } from '@/shared/auth/tenant-store'
import { time } from '@/shared/datetime/utils/time'

import type { MultiTenantAutomationResponse } from './types'
import type {
	CaseFilterState,
	CaseListParams,
} from '@/features/air/case-management/lib/case-list-params'
import type { TenantDetails, UserRequest } from '@/shared/auth/types'
import type { QueryClient } from '@tanstack/react-query'

/**
 * Query keys for multi-tenant views (separate namespace to avoid cache collisions).
 *
 * `tenants` is fingerprinted rather than embedded raw: an MSP-scoped selection
 * can carry many opaque tenant IDs, which bloat the cache and lag the Query
 * devtools. The content hash still busts cache when the selection changes.
 */
export const multiTenantKeys = {
	all: ['multi-tenant'] as const,
	// No `page`: the list is one infinite query, so every page lives under
	// this key, and a new tenant selection or filter is a new key that starts
	// at page 1.
	cases: (tenants?: string[], limit?: number, params: CaseListParams = {}) =>
		[
			...multiTenantKeys.all,
			'cases',
			{ tenants: fingerprint(tenants), limit, params },
		] as const,
	policies: (tenants?: string[]) =>
		[
			...multiTenantKeys.all,
			'policies',
			{ tenants: fingerprint(tenants) },
		] as const,
	automations: (tenants?: string[]) =>
		[
			...multiTenantKeys.all,
			'automations',
			{ tenants: fingerprint(tenants) },
		] as const,
}

/**
 * Query options for multi-tenant data fetching.
 * Uses tenant="all" path param + tenants array for X-AIR-Request-Tenants header.
 */
export const multiTenantQueries = {
	cases: (
		tenants?: string[],
		limit: number = 25,
		params: CaseListParams = {},
	) =>
		infiniteQueryOptions({
			queryKey: multiTenantKeys.cases(tenants, limit, params),
			queryFn: ({ pageParam, signal }) =>
				getCaseList({
					tenant: 'all',
					limit,
					page: pageParam,
					params,
					signal,
					tenants,
				}),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(1, 'm'),
			initialPageParam: 1,
			// `total` is exact only on a page with rows, so only an empty page
			// ends the list — same contract as the AIR Cases list.
			getNextPageParam: (lastPage, _allPages, lastPageParam) =>
				lastPage.cases.length === 0 ? undefined : lastPageParam + 1,
		}),
	policies: (tenants?: string[]) =>
		queryOptions({
			queryKey: multiTenantKeys.policies(tenants),
			queryFn: ({ signal }) => getPolicyList('all', signal, tenants),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			select: (data) => data.rules,
		}),
	automations: (tenants?: string[]) =>
		queryOptions({
			queryKey: multiTenantKeys.automations(tenants),
			queryFn: ({ signal }) =>
				getAutomationsList(
					'all',
					signal,
					undefined,
					undefined,
					undefined,
					tenants,
				),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			// The multi-tenant endpoint augments each automation with the owning
			// `tenant` (the shared single-tenant `AutomationResponse` type omits it).
			// Assert that contract here so consumers receive properly-typed rows.
			select: (data) => data.responses as Array<MultiTenantAutomationResponse>,
		}),
}

/**
 * Fetch cases across all tenants, one page at a time (infinite scroll)
 * @param tenants - optional tenant IDs to filter by
 * @param params - Filters and dates, from `toCaseListParams`
 * @param limit - items per page (default 25)
 */
export const useMultiTenantCases = (
	tenants: string[] | undefined,
	params: CaseListParams,
	limit: number = 25,
) => {
	return useSuspenseInfiniteQuery(
		multiTenantQueries.cases(tenants, limit, params),
	)
}

/**
 * The tenants a multi-tenant tab shows, for a route loader: the `tenants` in the
 * URL, or every allowed tenant when none is chosen — the tables' rule, so a
 * prefetch keys the list as the table does. Reads `/me` from the cache.
 */
export const getSelectedTenants = (
	queryClient: QueryClient,
	tenants: ReadonlyArray<string> | undefined,
): Array<string> =>
	tenants?.length
		? [...tenants]
		: (
				queryClient.getQueryData<UserRequest>(userKeys.me())?.allowed_tenants ??
				[]
			).map((t) => t.id)

/**
 * Warm a multi-tenant tab's Source options from its route loader: the
 * connectors of `tenants` and the catalog that names them. Not awaited — the
 * page does not wait for its filter options.
 */
export const prefetchTenantsLogSources = (
	queryClient: QueryClient,
	tenants: ReadonlyArray<string>,
) => {
	if (tenants.length === 0) return
	queryClient.prefetchQuery(integrationQueries.tenantsList(tenants))
	const tenant = getCurrentTenant()
	if (tenant) queryClient.prefetchQuery(integrationQueries.available(tenant))
}

/** One tenant's side of the Investigation rule. Details not loaded count as off. */
const hasInvestigations = (details: TenantDetails | undefined) =>
	Boolean(details?.features?.beta_enable_automated_investigations)

/**
 * {@link useAnyTenantInvestigationsEnabled}, read from the cache for a route
 * loader. Same rule, so the prefetch keys the list as the table will.
 */
const getAnyTenantInvestigationsEnabled = (
	queryClient: QueryClient,
	tenants: ReadonlyArray<string>,
) =>
	tenants.some((tenant) =>
		hasInvestigations(
			queryClient.getQueryData(tenantSessionQueries.details(tenant).queryKey),
		),
	)

/**
 * The multi-tenant layout's loader: warms every tab for the selected tenants.
 * The Cases list is warmed with no filter set, so a tab switch lands on a warm
 * cache; the Cases route warms the list for its own URL.
 */
export const prefetchMultiTenantTabs = (
	queryClient: QueryClient,
	tenants: ReadonlyArray<string> | undefined,
) => {
	const selected = getSelectedTenants(queryClient, tenants)
	if (selected.length === 0) return

	queryClient.prefetchInfiniteQuery(
		multiTenantQueries.cases(
			selected,
			25,
			toCaseListParams({}, { showInvestigation: false }),
		),
	)
	queryClient.prefetchQuery(multiTenantQueries.policies(selected))
	queryClient.prefetchQuery(multiTenantQueries.automations(selected))
}

/**
 * The Cases tab's loader: the Source options and the Case list for the filters
 * in the URL, with the params the table sends. Not awaited.
 *
 * The Investigation rule comes from the cached tenant details. A tenant not
 * cached yet counts as off, so with an Investigation filter set the prefetch
 * can miss the table's key; that costs one request.
 */
export const prefetchMultiTenantCases = (
	queryClient: QueryClient,
	search: CaseFilterState & { tenants?: ReadonlyArray<string> },
) => {
	const selected = getSelectedTenants(queryClient, search.tenants)
	const showInvestigation = getAnyTenantInvestigationsEnabled(
		queryClient,
		selected,
	)

	prefetchTenantsLogSources(queryClient, selected)

	if (selected.length === 0) return
	queryClient.prefetchInfiniteQuery(
		multiTenantQueries.cases(
			selected,
			25,
			toCaseListParams(search, { showInvestigation }),
		),
	)
}

/**
 * Whether any of `tenants` has automated investigations on — the rule for
 * offering the Investigation Case filter on this tab. The caller passes the
 * selected tenants, or every visible tenant when none is selected.
 *
 * Non-suspense on purpose: it gates one filter group, so it must not hold the
 * table back. Until a tenant's details land, that tenant counts as off. Each
 * details query is the one the tenant session already uses (cached for an hour),
 * so a tenant switched to earlier costs no request.
 */
export const useAnyTenantInvestigationsEnabled = (
	tenants: ReadonlyArray<string>,
) =>
	useQueries({
		queries: tenants.map((tenant) => tenantSessionQueries.details(tenant)),
		combine: (results) =>
			results.some((result) => hasInvestigations(result.data)),
	})

/**
 * Fetch policies across all tenants (selects rules from response)
 * @param tenants - optional tenant IDs to filter by
 */
export const useMultiTenantPolicies = (tenants?: string[]) => {
	return useSuspenseQuery(multiTenantQueries.policies(tenants))
}

/**
 * Fetch automations across all tenants
 * @param tenants - optional tenant IDs to filter by
 */
export const useMultiTenantAutomations = (tenants?: string[]) => {
	return useSuspenseQuery(multiTenantQueries.automations(tenants))
}
